import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { login, resetUserLogin } from "../../actions/userActions";
import "./AuthScreen.css";

function errorText(error = "") {
  if (!error) return "";
  if (typeof error === "string") return error;
  return error.message || "";
}

export default function LoginScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [localError, setLocalError] = useState("");

  const loginState = useSelector((state) => state.userLogin || {});
  const userAuth = useSelector((state) => state.userAuth || {});

  const { loading, error } = loginState;

  const {
    userInfo = null,
    isAuthenticated = false,
    loading: authLoading = false,
  } = userAuth;

  const userId = userInfo?.id || userInfo?._id;

  const redirect = useMemo(() => {
    const queryRedirect =
      new URLSearchParams(location.search).get("redirect") || "";

    if (queryRedirect) {
      return queryRedirect.startsWith("/") ? queryRedirect : "/";
    }

    const from = location.state?.from;

    if (typeof from === "string") {
      return from.startsWith("/") ? from : "/";
    }

    if (from?.pathname) {
      return `${from.pathname || "/"}${from.search || ""}${from.hash || ""}`;
    }

    return "/";
  }, [location.search, location.state]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, userId, navigate, redirect]);

  useEffect(() => {
    return () => {
      dispatch(resetUserLogin());
    };
  }, [dispatch]);

  const submitHandler = async (e) => {
    e.preventDefault();
    setLocalError("");

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setLocalError("Email address is required");
      return;
    }

    if (!password) {
      setLocalError("Password is required");
      return;
    }

    const result = await dispatch(login(trimmedEmail, password));

    if (result?.code === "EMAIL_NOT_VERIFIED") {
      navigate("/verify-email", {
        state: { email: trimmedEmail, from: redirect },
      });
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <div className="auth-grid">
          <div className="auth-panel auth-panel--form">
            <h1 className="auth-title">Sign In</h1>

            {(localError || error) && (
              <div className="auth-alert">{localError || errorText(error)}</div>
            )}

            <form className="auth-form" onSubmit={submitHandler}>
              <div className="auth-field">
                <label htmlFor="email">Your email*</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="auth-field">
                <label htmlFor="password">Password*</label>
                <div className="auth-password-wrap">
                  <input
                    id="password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="auth-eye"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="auth-btn auth-btn--primary"
                disabled={loading || authLoading}
              >
                {loading || authLoading ? "Signing in..." : "Sign in"}
              </button>

              <Link to="/forgot-password" className="auth-inline-link">
                Forgotten your password?
              </Link>
            </form>
          </div>

          <div className="auth-panel auth-panel--info">
            <h2 className="auth-side-title">New to Academy Costumes London?</h2>

            <p className="auth-copy">
              Create an account to receive these benefits:
            </p>

            <ul className="auth-benefits">
              <li>Check out faster with saved details</li>
              <li>Track orders and manage returns</li>
              <li>Save delivery addresses</li>
              <li>Get updates on new arrivals</li>
            </ul>

            <Link to="/register" className="auth-btn auth-btn--secondary">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
